import { useState, useEffect } from "react";

// All 2688×1242 — consistent width, no jumping
const screenshots = [
  "/progbots/screen1.png",
  "/progbots/screen2.png",
  "/progbots/screen5.png",
  "/progbots/screen6.png",
  "/progbots/screen9.png",
  "/progbots/screen10.png",
];

const features = [
  { icon: "⚙️", label: "Functions", desc: "Define reusable routines and call them anywhere" },
  { icon: "🔁", label: "Loops", desc: "Repeat commands efficiently — no busywork" },
  { icon: "🔀", label: "Conditionals", desc: "Make decisions with if/else logic" },
  { icon: "🌀", label: "Recursion", desc: "Functions that call themselves to solve complex paths" },
  { icon: "📦", label: "Parameters", desc: "Pass values into functions to make them flexible" },
  { icon: "📝", label: "Variables", desc: "Store and reuse values across your program" },
];

export default function ProgBotsHome() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % screenshots.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.badge}>FREE • iOS</div>
          <h1 style={styles.title}>ProgBots</h1>
          <p style={styles.tagline}>Code Robots. Solve Puzzles.</p>
          <p style={styles.subTagline}>
            A puzzle game that teaches real programming concepts — no keyboard required.
          </p>
          <a
            href="https://apps.apple.com/app/progbots/id6768014459"
            style={styles.appStoreBtn}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              style={{ height: 48 }}
            />
          </a>
        </div>

        {/* Screenshot carousel */}
        <div style={styles.carouselWrap}>
          <div style={styles.carousel}>
            <img
              src={screenshots[current]}
              alt={`ProgBots screenshot ${current + 1}`}
              style={styles.carouselImg}
            />
          </div>
          <div style={styles.dots}>
            {screenshots.map((_, i) => (
              <span
                key={i}
                onClick={() => setCurrent(i)}
                style={{ ...styles.dot, ...(i === current ? styles.dotActive : {}) }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>A Real Programmer's Toolbox</h2>
        <p style={styles.sectionSub}>
          As you progress through ProgBots, you unlock every tool a real programmer uses —
          not as theory, but as mechanics you need to solve the next puzzle.
        </p>
        <div style={styles.featureGrid}>
          {features.map((f) => (
            <div key={f.label} style={styles.featureCard}>
              <span style={styles.featureIcon}>{f.icon}</span>
              <h3 style={styles.featureLabel}>{f.label}</h3>
              <p style={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section style={{ ...styles.section, ...styles.sectionDark }}>
        <h2 style={styles.sectionTitle}>What is ProgBots?</h2>
        <p style={styles.bodyText}>
          ProgBots is a puzzle game where you write programs to control robots on a grid.
          Each level is a new challenge — navigate obstacles, avoid traps, reach the goal.
          The catch? You have to think like a programmer to get there.
        </p>
        <p style={styles.bodyText}>
          Whether you're 8 or 80, ProgBots makes the fundamentals of programming click —
          not through tutorials or theory, but through puzzles that demand you actually use them.
        </p>
      </section>

      {/* CTA */}
      <section style={styles.cta}>
        <h2 style={styles.ctaTitle}>Ready to program your bot?</h2>
        <a
          href="https://apps.apple.com/app/progbots/id6768014459"
          style={styles.appStoreBtn}
          target="_blank"
          rel="noreferrer"
        >
          <img
            src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
            alt="Download on the App Store"
            style={{ height: 48 }}
          />
        </a>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <a href="/policyProgBots" style={styles.footerLink}>Privacy Policy</a>
        <span style={styles.footerSep}>·</span>
        <span style={styles.footerText}>© 2026 Petar Takev</span>
      </footer>
    </div>
  );
}

const colors = {
  bg: "#0d0f14",
  surface: "#161a24",
  accent: "#f0a500",
  accentAlt: "#e05c00",
  text: "#f0f0f0",
  muted: "#9aa0b0",
  border: "#2a2f3d",
};

const styles = {
  page: {
    background: colors.bg,
    color: colors.text,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: "100vh",
  },
  hero: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: 48,
    padding: "80px 40px 60px",
    background: `linear-gradient(135deg, ${colors.bg} 0%, #1a1f30 100%)`,
    borderBottom: `1px solid ${colors.border}`,
  },
  heroContent: {
    maxWidth: 480,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  badge: {
    display: "inline-block",
    background: colors.accentAlt,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    padding: "4px 12px",
    borderRadius: 20,
    width: "fit-content",
  },
  title: {
    fontSize: "clamp(48px, 8vw, 80px)",
    fontWeight: 800,
    margin: 0,
    background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentAlt})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: 1.1,
    paddingBottom: "0.1em",
  },
  tagline: {
    fontSize: 22,
    fontWeight: 600,
    margin: 0,
    color: colors.text,
  },
  subTagline: {
    fontSize: 16,
    color: colors.muted,
    margin: 0,
    lineHeight: 1.6,
  },
  appStoreBtn: {
    display: "inline-block",
    marginTop: 8,
  },
  carouselWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  },
  carousel: {
    width: "min(560px, 90vw)",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
    border: `1px solid ${colors.border}`,
  },
  carouselImg: {
    width: "100%",
    display: "block",
  },
  dots: {
    display: "flex",
    gap: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: colors.border,
    cursor: "pointer",
    transition: "background 0.2s",
  },
  dotActive: {
    background: colors.accent,
  },
  section: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "72px 40px",
  },
  sectionDark: {
    maxWidth: "100%",
    background: colors.surface,
  },
  sectionTitle: {
    fontSize: "clamp(24px, 4vw, 36px)",
    fontWeight: 700,
    margin: "0 0 16px",
    textAlign: "center",
  },
  sectionSub: {
    color: colors.muted,
    textAlign: "center",
    maxWidth: 640,
    margin: "0 auto 48px",
    lineHeight: 1.7,
    fontSize: 16,
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 24,
  },
  featureCard: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: "28px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    transition: "border-color 0.2s",
  },
  featureIcon: {
    fontSize: 28,
  },
  featureLabel: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
    color: colors.accent,
  },
  featureDesc: {
    fontSize: 14,
    color: colors.muted,
    margin: 0,
    lineHeight: 1.6,
  },
  bodyText: {
    color: colors.muted,
    lineHeight: 1.8,
    fontSize: 16,
    maxWidth: 700,
    margin: "0 auto 16px",
    textAlign: "center",
  },
  cta: {
    textAlign: "center",
    padding: "80px 40px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 24,
  },
  ctaTitle: {
    fontSize: "clamp(22px, 4vw, 32px)",
    fontWeight: 700,
    margin: 0,
  },
  footer: {
    textAlign: "center",
    padding: "24px 40px",
    borderTop: `1px solid ${colors.border}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  footerLink: {
    color: colors.muted,
    textDecoration: "none",
    fontSize: 13,
  },
  footerSep: {
    color: colors.border,
  },
  footerText: {
    color: colors.muted,
    fontSize: 13,
  },
};
